import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { B, PILLARS } from "../data/pillars";
import { getRiskLevel } from "../data/recommendations";
import SectionHeader from "./SectionHeader";
import Gauge from "./Gauge";
import PillarBar from "./PillarBar";

const LEVEL_LABELS = ["Reactive", "Emerging", "Scaling", "Proactive", "Optimized"];
const LEVEL_COLORS = [B.red, B.orange, B.yellow, B.green, B.teal];

export default function PillarDeepDive({ answers, pillarScores, confidenceMap, intake }) {
  const [activePillar, setActivePillar] = useState(PILLARS[0].id);
  const pillar = PILLARS.find(p => p.id === activePillar);
  const pillarScore = pillarScores[activePillar];

  return (
    <div style={{
      background: "#1A2E4A", borderRadius: 16, border: "1px solid #1E3A5F",
      padding: "clamp(20px,4vw,32px)", marginBottom: 24, boxSizing: "border-box",
    }}>
      <SectionHeader num={4} title="Pillar Deep Dive" sub="Question-Level Analysis by Domain" />

      {/* Pillar tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24, flexWrap: "wrap" }}>
        {PILLARS.map(p => {
          const s = pillarScores[p.id];
          const isActive = activePillar === p.id;
          return (
            <button
              key={p.id}
              onClick={() => setActivePillar(p.id)}
              style={{
                background: isActive ? p.color + "20" : "#122240",
                border: `2px solid ${isActive ? p.color : "#1E3A5F"}`,
                borderRadius: 10, padding: "10px 16px",
                color: isActive ? B.white : B.subText,
                fontSize: 12, fontWeight: 700, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 8,
                transition: "all 0.15s",
              }}
            >
              <span>{p.icon}</span>
              <span>{p.label}</span>
              <span style={{
                background: isActive ? p.color : "#1E3A5F",
                color: isActive ? B.white : B.subText,
                borderRadius: 4, padding: "1px 6px", fontSize: 11, fontWeight: 800,
              }}>{s.toFixed(1)}</span>
            </button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activePillar}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
        >
          {/* Pillar overview */}
          <div style={{
            display: "flex", flexWrap: "wrap", gap: 20,
            background: "#122240", borderRadius: 12, padding: "20px 24px",
            marginBottom: 20, border: `1px solid ${pillar.color}30`,
          }}>
            <div style={{ flexShrink: 0 }}>
              <Gauge score={pillarScore} max={5} size={120} color={pillar.color} />
            </div>
            <div style={{ flex: "1 1 240px" }}>
              <div style={{
                fontSize: 11, color: pillar.color, fontWeight: 800,
                letterSpacing: 2, marginBottom: 8,
              }}>
                {pillar.icon} {pillar.label.toUpperCase()} · {pillar.sub.toUpperCase()}
              </div>
              <div style={{
                fontSize: 18, fontWeight: 800, color: B.white, marginBottom: 6,
              }}>
                OML {pillarScore.toFixed(1)} — {LEVEL_LABELS[Math.round(pillarScore) - 1] || "Reactive"}
              </div>
              <p style={{ color: B.lightGray, fontSize: 13, lineHeight: 1.7, margin: 0 }}>
                {pillarScore < 2
                  ? `${intake.company}'s ${pillar.label.toLowerCase()} posture is reactive and underdeveloped. Immediate standardization is required.`
                  : pillarScore < 3
                  ? `Emerging controls are in place but inconsistently applied. A structured improvement plan will close key gaps.`
                  : pillarScore < 4
                  ? `Core capabilities are established. Focus on automation, governance, and continuous improvement.`
                  : `Strong posture in this domain. Maintain and optimize — consider sharing best practices across the organization.`}
              </p>
            </div>
          </div>

          {/* Question breakdown */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {pillar.questions.map((q, qi) => {
              const score = answers[q.id] !== undefined ? answers[q.id] + 1 : 1;
              const isUnsure = confidenceMap[q.id] === false;
              const c = LEVEL_COLORS[score - 1];
              const risk = getRiskLevel(score);
              const nextLevel = score < 5 ? score + 1 : null;

              return (
                <div key={q.id} style={{
                  background: "#0D1B2E", borderRadius: 12, padding: "18px 20px",
                  border: `1px solid ${c}25`,
                }}>
                  <div style={{
                    display: "flex", justifyContent: "space-between",
                    alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 12,
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, color: B.subText, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>
                        QUESTION {qi + 1} OF {pillar.questions.length}
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: B.white }}>{q.label}</div>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0, flexWrap: "wrap" }}>
                      {isUnsure && (
                        <span style={{
                          background: B.yellow + "20", color: B.yellow,
                          fontSize: 10, fontWeight: 700, padding: "3px 8px",
                          borderRadius: 4, border: `1px solid ${B.yellow}40`,
                        }}>⚠ VALIDATE</span>
                      )}
                      <span style={{
                        background: c + "20", color: c,
                        fontSize: 12, fontWeight: 800, padding: "4px 12px",
                        borderRadius: 6, border: `1px solid ${c}40`,
                      }}>OML {score} · {LEVEL_LABELS[score - 1]}</span>
                    </div>
                  </div>

                  {/* Current answer */}
                  <div style={{
                    background: c + "12", borderLeft: `3px solid ${c}`,
                    borderRadius: "0 8px 8px 0", padding: "10px 14px", marginBottom: 10,
                  }}>
                    <div style={{ fontSize: 10, color: c, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>
                      CURRENT STATE
                    </div>
                    <div style={{ fontSize: 13, color: B.white, fontWeight: 600 }}>
                      {q.opts[answers[q.id] !== undefined ? answers[q.id] : 0]}
                    </div>
                  </div>

                  {/* Next step */}
                  {nextLevel && (
                    <div style={{
                      background: B.green + "08", borderLeft: `3px solid ${B.green}50`,
                      borderRadius: "0 8px 8px 0", padding: "10px 14px",
                    }}>
                      <div style={{ fontSize: 10, color: B.green, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>
                        NEXT LEVEL (OML {nextLevel})
                      </div>
                      <div style={{ fontSize: 13, color: B.lightGray }}>
                        {q.opts[nextLevel - 1]}
                      </div>
                    </div>
                  )}

                  {/* Score bar */}
                  <div style={{ marginTop: 12, background: "#1E3A5F", borderRadius: 4, height: 6, overflow: "hidden" }}>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: (score / 5 * 100) + "%" }}
                      transition={{ duration: 0.9, ease: "easeOut" }}
                      style={{ height: "100%", background: c, borderRadius: 4 }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pillar overall bar */}
          <div style={{ marginTop: 20 }}>
            <PillarBar
              label={`${pillar.icon} ${pillar.label} (${pillar.sub})`}
              score={pillarScore} color={pillar.color}
            />
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}